import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _merge from 'lodash/merge';
import { selectors as i18nSelectors, translator } from 'mage-i18n';
import { AsYouType } from 'libphonenumber-js/min';

import {
  ButtonPrimary,
  ButtonSecondary,
  SpacerHorizontal,
  SpacerVertical,
} from '@klarna/bubble-ui';
import {
  analytics,
  defaultContainerPropTypes,
  FieldsetForObject,
  formUtils,
  HorizontalWrapper,
  TextArea,
  Input,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { phoneNumber as phoneNumberValidator } from '@klarna/mf-validations';

import { validators } from '@/flow/validation';
import { LEAD_ONBOARDING_TYPES } from '@/constants';
import { ContainerTemplate, withLeadUpdateOnNext } from '@/components';
import { getUserData } from '@/selectors/data';
import * as a from '@/actions/signup';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import { CY_CONTACT_DETAILS_CONTAINER_IDS } from './constants';

const tPrefix = 'products-fe.containers.contact_details';

export class ContactDetails extends React.Component {
  constructor(props) {
    super(props);

    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onNext = this.onNext.bind(this);
    this.prepareFields = this.prepareFields.bind(this);

    const { fields } = this.prepareFields();

    this.state = { fields, language: props.language };
  }

  prepareFields() {
    const { userData = {}, t } = this.props;
    const { fields } = this.state || {};
    const values = formUtils.getMappedValues(fields);
    const { fullName = '', phone = '', extraInfo = '' } = _merge(userData, values);
    const phonePrefix = this.props.leadData?.country === 'US' ? '+1' : '';

    return {
      fields: {
        fullName: {
          component: Input,
          size: 1,
          type: 'text',
          label: t(`${tPrefix}.fields.full_name.label`),
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: t(`${tPrefix}.fields.full_name.errors.required`),
            },
          ],
          isInitialized: false,
          isValid: false,
          value: fullName,
          extraProps: { ...generateCyElemId(CY_CONTACT_DETAILS_CONTAINER_IDS.FULL_NAME, window) },
        },
        phone: {
          component: Input,
          type: 'tel',
          size: 1,
          label: t(`${tPrefix}.fields.phone.label`),
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: t(`${tPrefix}.fields.phone.errors.required`),
            },
            {
              callback: phoneNumberValidator.validate,
              errorMessage: t(`${tPrefix}.fields.phone.errors.invalid`),
            },
          ],
          isInitialized: false,
          isValid: false,
          value: phone || phonePrefix,
          extraProps: {
            ...generateCyElemId(CY_CONTACT_DETAILS_CONTAINER_IDS.PHONE, window),
            tooltipText: t(`${tPrefix}.fields.phone.tooltip`),
          },
        },
        extraInfo: {
          component: TextArea,
          size: 1,
          type: 'text',
          label: t(`${tPrefix}.fields.tell_us_more.label`),
          validators: [],
          isInitialized: true,
          isValid: true,
          value: extraInfo,
          extraProps: {
            height: 120,
            ...generateCyElemId(CY_CONTACT_DETAILS_CONTAINER_IDS.TELL_US_MORE, window),
          },
        },
      },
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.language !== this.props.language) {
      const { fields } = this.prepareFields();
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({ fields, language: this.props.language });
    }
  }

  onBlur(key) {
    formUtils.onBlurFields(key, this.state, fields => this.setState({ fields }));
  }

  onChange(key, value) {
    const formatedValue = key === 'phone' ? new AsYouType().input(value) : value;
    formUtils.onChangeFields(key, formatedValue, this.state, fields => this.setState({ fields }));
  }

  onNext() {
    this.setState({ fields: formUtils.initForm(formUtils.initializeFields(this.state.fields)) });

    const { onSubmit, onNext } = this.props;
    const { fullName, phone, extraInfo } = formUtils.getMappedValues(this.state.fields);

    formUtils.callToActionOnNextWrapper(
      this.state.fields,
      () => {
        const phoneNumber = String(phone).replace(/\s/g, '');
        analytics.logEvent(ANALYTICS_EVENTS.contactDetails, { fullName, phoneNumber, extraInfo });
        return onSubmit({ fullName, phone, extraInfo }).then(() =>
          onNext({
            leadData: {
              firstName: fullName,
              phoneNumber,
              extraInfo,
              leadOnboardingType: LEAD_ONBOARDING_TYPES.MANUAL,
            },
          }),
        );
      },
      formUtils.isFormInitializedAndValid(this.state.fields),
    );
  }

  render() {
    const { isDisabled, isLoading, onBack, t } = this.props;
    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title_line_1`) + ' ' + t(`${tPrefix}.title_line_2`)}
        subtitle={t(`${tPrefix}.subtitle`)}
        showRecaptchaFooter
      >
        <FieldsetForObject
          name="contact-details"
          disabled={isDisabled}
          fields={this.state.fields}
          onChange={this.onChange}
          onBlur={this.onBlur}
        />
        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          <ButtonSecondary
            onClick={onBack}
            disabled={isDisabled}
            {...generateCyElemId(CY_CONTACT_DETAILS_CONTAINER_IDS.BTN_BACK, window)}
          >
            {t(`${tPrefix}.buttons.back`)}
          </ButtonSecondary>
          <SpacerHorizontal medium />
          <ButtonPrimary
            onClick={this.onNext}
            loading={isLoading}
            {...generateCyElemId(CY_CONTACT_DETAILS_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.submit`)}
          </ButtonPrimary>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

ContactDetails.propTypes = {
  ...defaultContainerPropTypes,
  language: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default compose(
  connect(
    state => ({
      userData: getUserData(state),
      language: i18nSelectors.language(state),
      t: translator(state),
    }),
    dispatch => ({
      onSubmit: ({ fullName, phone, extraInfo }) => {
        return new Promise(resolve => {
          a.onChangeFormValue('fullName', fullName)(dispatch);
          a.onChangeFormValue('phone', phone)(dispatch);
          a.onChangeFormValue('extraInfo', extraInfo)(dispatch);
          a.onChangeFormValue('leadOnboardingType', LEAD_ONBOARDING_TYPES.MANUAL)(dispatch);
          resolve();
        });
      },
    }),
  ),
  withLeadUpdateOnNext,
)(ContactDetails);
